import type {
  Absence,
  Appointment,
  ScheduleEntry,
  ScheduleResult,
  TaskListItem,
} from '@myapp/contract';
import { getAbsenceCategoryLabel } from '../../../entities/absence';
import { workOrderAddress } from '../../../shared/helpers/address';
import { isWorkingDay } from '../../../shared/lib/workingDays';
import {
  clampSpanToShift,
  formatTimeHHmm,
  shiftWindow,
  resolveBlockTimes,
  toBoardSpan,
  workWindow,
  type BoardSpan,
} from '../../../shared/lib/schedule';
import { getBlockColors } from './blockColors';
import { buildTooltip } from './blockTooltip';
import { showsReschedulingState } from './reschedulingDisplay';
import type { WeekCellModel, WeekChip, WeekRowModel } from './weekTypes';

/**
 * `ScheduleResult` → one row per technician, one cell per weekday.
 *
 * The same join as the day board's `buildRows` — walk `technicians[].entries` and look the owner up
 * in the top-level arrays — run once per day column. **Bucketing an entry into a column is
 * `toBoardSpan`**: it returns `null` when the entry falls outside that day's drawn window, so a
 * multi-day Urlaub lands one chip in each day it covers, and the week hides exactly the entries the
 * day board hides. Nothing here special-cases a date boundary.
 *
 * `packIntoLanes` has no counterpart: CSS grid gives every row the height of its tallest cell, so
 * there is nothing to measure.
 */
export function buildWeekRows(
  result: ScheduleResult | undefined,
  days: string[],
  todayIso: string,
): WeekRowModel[] {
  if (!result) return [];

  const appointmentsById = new Map(result.appointments.map((a) => [a.id, a]));
  const tasksById = new Map(result.tasks.map((t) => [t.id, t]));
  const absencesById = new Map(result.absences.map((a) => [a.id, a]));

  return result.technicians.map((technician) => {
    const shift = shiftWindow(technician.shiftStart, technician.shiftEnd);

    const cells: WeekCellModel[] = days.map((dayIso) => {
      const working = isWorkingDay(technician.workingDays, dayIso);
      const chips: WeekChip[] = [];
      const fullDayAbsences: WeekChip[] = [];

      technician.entries.forEach((entry, index) => {
        const span = toBoardSpan(entry.startsAt, entry.endsAt, dayIso);
        if (!span) return; // not this day, or outside the drawn hours entirely

        const appointment =
          entry.appointmentId != null ? appointmentsById.get(entry.appointmentId) ?? null : null;
        const task = entry.taskId != null ? tasksById.get(entry.taskId) ?? null : null;
        const absence = entry.absenceId != null ? absencesById.get(entry.absenceId) ?? null : null;

        const chip = toChip(entry, span, dayIso, index, appointment, task, absence);

        // A Krankmeldung is stored as whole calendar days, so on the day board it is clamped onto
        // the shift rather than drawn across the whole window. Here the equivalent is: if what is
        // left after that clamp is the entire shift, it is the day, not an item in it.
        if (entry.type === 'ABSENCE' && coversWholeShift(span, working, shift)) {
          fullDayAbsences.push(chip);
          return;
        }
        // An absence on a day the technician does not work has no work time to occupy — the cell is
        // already shaded end to end, so drawing it would only add noise.
        if (entry.type === 'ABSENCE' && !working) return;

        chips.push(chip);
      });

      chips.sort((a, b) => a.startMin - b.startMin || a.key.localeCompare(b.key));

      return {
        dayIso,
        working,
        isToday: dayIso === todayIso,
        fullDayAbsences,
        chips,
      };
    });

    return {
      technicianId: technician.technicianId,
      technicianName: technician.technicianName,
      technicianAvatar: technician.technicianAvatar,
      cells,
    };
  });
}

/**
 * True when the absence, once clamped onto the shift, leaves nothing of the working day free.
 * `null` shift — no times on the profile — falls back to the entry's own `allDay` flag rather than
 * guessing at boundaries that were never configured.
 */
function coversWholeShift(
  span: BoardSpan,
  working: boolean,
  shift: { startMin: number; endMin: number } | null,
): boolean {
  if (!working) return false;
  if (!shift) return span.clippedStart && span.clippedEnd;

  const clamped = clampSpanToShift(span, shift);
  if (!clamped) return false;
  return clamped.startMin <= shift.startMin && clamped.endMin >= shift.endMin;
}

/**
 * One entry → one chip.
 *
 * The subject is the same field the day board's work segment shows, so the two views never disagree
 * about what a block *is*: an address for a Termin — never `entry.label`, which is the server's
 * `Termin 20266555` string — and the title for an Aufgabe.
 *
 * The **time** obeys the same rule: it is the `workWindow`, not `startsAt`/`endsAt`. The occupancy
 * block is what the day board draws and what buckets this chip into its column; what a dispatcher
 * reads is when the Termin is, which is travel-minutes later.
 */
function toChip(
  entry: ScheduleEntry,
  span: BoardSpan,
  dayIso: string,
  index: number,
  appointment: Appointment | null,
  task: TaskListItem | null,
  absence: Absence | null,
): WeekChip {
  const status = appointment?.status ?? task?.status ?? null;
  const external = appointment?.workOrder?.external;

  // The label is about the work, so the "does it fit the drawn window" test has to be about the
  // work too: a block whose Anfahrt begins at 05:45 is clipped, yet its 06:15 work start is on
  // screen and honest. Suppressed at *either* edge — half a range (`09:00–22:00` over work that
  // really runs to 23:30) reads as a bug, the same rule the day board's single-bar segment applies.
  const work = workWindow(entry);
  const workSpan = toBoardSpan(work.startsAt, work.endsAt, dayIso);
  const time =
    workSpan && !workSpan.clippedStart && !workSpan.clippedEnd
      ? `${formatTimeHHmm(work.startsAt)}–${formatTimeHHmm(work.endsAt)}`
      : null;

  return {
    key: `${dayIso}:${entry.type}:${entry.id ?? `p${index}`}`,
    kind: entry.type,
    entry,
    startMin: span.startMin,
    time,
    label: buildLabel(entry, appointment, task, absence),
    subLabel: entry.type === 'APPOINTMENT' ? external?.gewerkeText ?? undefined : undefined,
    color: getBlockColors(entry.type, status).workColor,
    needsRescheduling: showsReschedulingState(entry),
    // Any open phase — a chip has no strips, so "driving out" and "working" look the same here.
    inProgress: resolveBlockTimes(entry).isSessionOpen,
    tooltip: buildTooltip(entry, appointment, task, absence),
  };
}

function buildLabel(
  entry: ScheduleEntry,
  appointment: Appointment | null,
  task: TaskListItem | null,
  absence: Absence | null,
): string {
  switch (entry.type) {
    case 'APPOINTMENT': {
      const external = appointment?.workOrder?.external;
      return workOrderAddress(external) ?? 'Termin';
    }
    case 'TASK':
      return task?.title ?? 'Aufgabe';
    case 'ABSENCE':
      return getAbsenceCategoryLabel(absence?.category ?? entry.category);
    case 'BREAK':
    default:
      return entry.label ?? 'Pause';
  }
}
